import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { ConfigurableListItem, ConfigurableListType } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface ConfigurableListResponse {
  items: ConfigurableListItem[];
}

interface ConfigurableListItemResponse {
  item: ConfigurableListItem;
}

export const configurableListKeys = {
  all: ['configurable-lists'] as const,
  list: (type: ConfigurableListType) => [...configurableListKeys.all, type] as const,
};

export function useConfigurableList(type: ConfigurableListType) {
  return useQuery({
    queryKey: configurableListKeys.list(type),
    queryFn: async () => {
      const response = await apiClient.get<ConfigurableListResponse>(`/configurable-lists?type=${type}`);
      return response.items;
    },
  });
}

export function useAddConfigurableListItem(type: ConfigurableListType) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (value: string) => {
      const response = await apiClient.post<ConfigurableListItemResponse>('/configurable-lists', {
        listType: type,
        value,
      });
      return response.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configurableListKeys.list(type) });
      toast({ title: 'Item added' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add item.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateConfigurableListItem(type: ConfigurableListType) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string }) => {
      const response = await apiClient.put<ConfigurableListItemResponse>(`/configurable-lists/${id}`, { value });
      return response.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configurableListKeys.list(type) });
      toast({ title: 'Item updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update item.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteConfigurableListItem(type: ConfigurableListType) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/configurable-lists/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configurableListKeys.list(type) });
      toast({ title: 'Item deleted' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete item.',
        variant: 'destructive',
      });
    },
  });
}

/** Convenience hook: returns project types as string[] */
export function useProjectTypes() {
  const { data, ...rest } = useConfigurableList('project_type');
  return {
    ...rest,
    data: data?.map((item) => item.value) ?? [],
  };
}

/** Convenience hook: returns line item categories as string[] */
export function useLineItemCategories() {
  const { data, ...rest } = useConfigurableList('line_item_category');
  return {
    ...rest,
    data: data?.map((item) => item.value) ?? [],
  };
}
